import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Login as LoginIcon,
  PersonAdd,
} from '@mui/icons-material';
import { athleteAPI } from '../api/athlete.api';
import { useAuth } from '../context/AuthContext';

/**
 * Layout and styling
 */
const PageWrapper = styled(Box)({
  minHeight: '100vh',
  backgroundColor: '#f8f9fa',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem 0',
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(41, 30, 108, 0.15)',
  backgroundColor: '#ffffff',
  border: '1px solid #e0e0e0',
  maxWidth: '480px',
  width: '100%',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1),
    '& fieldset': { borderColor: '#e0e0e0' },
    '&:hover fieldset': { borderColor: '#291e6c' },
    '&.Mui-focused fieldset': { borderColor: '#291e6c' },
  },
}));

const StyledButton = styled(Button)({
  padding: '14px 32px',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  fontFamily: "'Oswald', sans-serif",
  backgroundColor: '#291e6c',
  color: 'white',
  '&:hover': { backgroundColor: '#1f1750' },
});

const Login = () => {
  const { login } = useAuth(); // Add this line
  const navigate = useNavigate();

  // Login form state
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Forgot Password dialog state
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1: email, 2: otp, 3: new password
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotOTP, setForgotOTP] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  /**
   * Login form handlers
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (
      !formData.email ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    ) {
      newErrors.email = 'Valid email is required';
    }

    if (!formData.password || formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    
    try {
      const response = await athleteAPI.login(formData);
      
      if (response.success) {
        localStorage.setItem('athleteId', response.data.athleteId );
        login(
          response.data, 
          response.data.token, 
          'athlete'
        );
        
        toast.success('Login successful!');
        
        setTimeout(() => {
          if (response.data.paymentStatus === 'pending') {
            navigate('/payment');
          } else {
            navigate('/dashboard');
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(
        error.response?.data?.message ||
          'Login failed. Please check your credentials.'
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Forgot Password handlers
   */

  const resetForgotState = () => {
    setForgotStep(1);
    setForgotEmail('');
    setForgotOTP('');
    setResetToken('');
    setNewPassword('');
    setConfirmPassword('');
    setForgotError('');
    setForgotLoading(false);
  };

  const handleForgotOpen = () => {
    resetForgotState();
    setForgotOpen(true);
  };

  const handleForgotClose = () => {
    setForgotOpen(false);
  };

  // Step 1: send OTP
  const handleSendOTP = async () => {
    if (
      !forgotEmail ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail)
    ) {
      setForgotError('Valid email is required');
      return;
    }

    setForgotLoading(true);
    setForgotError('');
    try {
      await athleteAPI.forgotPassword({ email: forgotEmail });
      toast.success('Password reset OTP sent to your email');
      setForgotStep(2);
    } catch (error) {
      console.error('Forgot password error:', error);
      setForgotError(
        error.response?.data?.message || 'Failed to send reset instructions.'
      );
    } finally {
      setForgotLoading(false);
    }
  };

  // Step 2: verify OTP and get resetToken
  const handleVerifyOTP = async () => {
    if (!forgotOTP || forgotOTP.length < 4) {
      setForgotError('Please enter the OTP sent to your email');
      return;
    }

    setForgotLoading(true);
    setForgotError('');
    try {
      const response = await athleteAPI.verifyPasswordResetOTP({
        email: forgotEmail,
        otp: forgotOTP,
      });

      if (response.success && response.data?.resetToken) {
        setResetToken(response.data.resetToken);
        toast.success('OTP verified successfully');
        setForgotStep(3);
      } else {
        setForgotError('Invalid response from server. Please try again.');
      }
    } catch (error) {
      console.error('Verify OTP error:', error);
      setForgotError(
        error.response?.data?.message || 'Failed to verify OTP.'
      );
    } finally {
      setForgotLoading(false);
    }
  };

  // Step 3: reset password using resetToken
  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      setForgotError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setForgotError('Password and confirm password must match');
      return;
    }
    if (!resetToken) {
      setForgotError('Missing reset token. Please restart the process.');
      return;
    }

    setForgotLoading(true);
    setForgotError('');
    try {
      const response = await athleteAPI.resetPassword({
        email: forgotEmail,
        resetToken,
        newPassword,
      });

      if (response.success) {
        toast.success('Password reset successfully. Please login with your new password.');
        setForgotOpen(false);
        resetForgotState();
      } else {
        setForgotError(
          response.message || 'Failed to reset password. Please try again.'
        );
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setForgotError(
        error.response?.data?.message || 'Failed to reset password.'
      );
    } finally {
      setForgotLoading(false);
    }
  };

  // Decide which action to run based on the current step
  const handleForgotPrimaryAction = () => {
    if (forgotStep === 1) return handleSendOTP();
    if (forgotStep === 2) return handleVerifyOTP();
    if (forgotStep === 3) return handleResetPassword();
  };

  const primaryButtonLabel =
    forgotStep === 1
      ? 'Send OTP'
      : forgotStep === 2
      ? 'Verify OTP'
      : 'Reset Password';

  return (
    <PageWrapper>
      <Container maxWidth="sm">
        <StyledPaper>
          <Box textAlign="center" mb={4}>
            <LoginIcon sx={{ fontSize: 40, color: '#291e6c' }} />
            <Typography
              variant="h4"
              sx={{
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 800,
                color: '#291e6c',
                mb: 1,
              }}
            >
              ATHLETE LOGIN
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Sign in to access your dashboard
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit}>
            <StyledTextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: '#291e6c' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />

            <StyledTextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              error={!!errors.password}
              helperText={errors.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: '#291e6c' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />

            <Box textAlign="right" mb={2}>
              <Link
                component="button"
                type="button" // important: do NOT submit the form
                variant="body2"
                onClick={handleForgotOpen}
                sx={{ color: '#291e6c' }}
              >
                Forgot Password?
              </Link>
            </Box>

            <StyledButton
              fullWidth
              type="submit"
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                ) : (
                  <LoginIcon />
                )
              }
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </StyledButton>
          </Box>

          <Divider sx={{ my: 3 }}>OR</Divider>

          <Button
            variant="outlined"
            fullWidth
            startIcon={<PersonAdd />}
            onClick={() => navigate('/register')}
            sx={{
              borderColor: '#291e6c',
              color: '#291e6c',
              fontFamily: "'Oswald', sans-serif",
              fontWeight: 600,
              padding: '12px 32px',
              '&:hover': {
                borderColor: '#1f1750',
                backgroundColor: 'rgba(41, 30, 108, 0.05)',
              },
            }}
          >
            New Registration
          </Button>

          <Box mt={3}>
            <Alert severity="info">
              <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                <li>
                  If you have pending payment, you&apos;ll be redirected to the
                  payment page
                </li>
                <li>Use your registered email and password to login</li>
              </ul>
            </Alert>
          </Box>
        </StyledPaper>

        {/* Forgot Password Dialog */}
        <Dialog open={forgotOpen} onClose={handleForgotClose} fullWidth maxWidth="xs">
          <DialogTitle>Forgot Password</DialogTitle>
          <DialogContent>
            {forgotStep === 1 && (
              <>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Enter your registered email address to receive an OTP for password reset.
                </Typography>
                <TextField
                  autoFocus
                  margin="dense"
                  label="Registered Email"
                  type="email"
                  fullWidth
                  variant="outlined"
                  value={forgotEmail}
                  onChange={(e) => {
                    setForgotEmail(e.target.value);
                    if (forgotError) setForgotError('');
                  }}
                  error={!!forgotError}
                  helperText={forgotError}
                />
              </>
            )}

            {forgotStep === 2 && (
              <>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  An OTP has been sent to <strong>{forgotEmail}</strong>. Enter it below to verify.
                </Typography>
                <TextField
                  margin="dense"
                  label="OTP"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={forgotOTP}
                  onChange={(e) => {
                    setForgotOTP(e.target.value);
                    if (forgotError) setForgotError('');
                  }}
                  error={!!forgotError}
                  helperText={forgotError}
                />
              </>
            )}

            {forgotStep === 3 && (
              <>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Enter your new password below.
                </Typography>
                <TextField
                  margin="dense"
                  label="New Password"
                  type="password"
                  fullWidth
                  variant="outlined"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    if (forgotError) setForgotError('');
                  }}
                  sx={{ mb: 2 }}
                />
                <TextField
                  margin="dense"
                  label="Confirm New Password"
                  type="password"
                  fullWidth
                  variant="outlined"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (forgotError) setForgotError('');
                  }}
                  error={!!forgotError}
                  helperText={forgotError}
                />
              </>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={handleForgotClose} color="secondary">
              Cancel
            </Button>
            <Button
              onClick={handleForgotPrimaryAction}
              color="primary"
              disabled={forgotLoading}
              startIcon={
                forgotLoading ? <CircularProgress size={20} /> : null
              }
            >
              {primaryButtonLabel}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </PageWrapper>
  );
};

export default Login;
